import NetInfo, { NetInfoState } from '@react-native-community/netinfo';
import { offlineDatabase } from './offlineDatabase';
import {
  ReviewTemplate,
  ReviewCategory,
  ReviewItem,
  Room,
  HomeReview,
} from '../types';
import { safeNegativeId } from '../utils/idUtils';
import { FULL_REVIEW_TEMPLATES_PATH } from './templateApiPaths';

/**
 * Cache configuration interface
 */
export interface CacheConfig {
  templates: {
    ttlHours: number;
    autoRefresh: boolean;
  };
  categories: {
    ttlHours: number;
    autoRefresh: boolean;
  };
  items: {
    ttlHours: number;
    autoRefresh: boolean;
  };
  rooms: {
    ttlHours: number;
    autoRefresh: boolean;
  };
  reviews: {
    ttlHours: number;
    autoRefresh: boolean;
  };
}

/**
 * Cache validity result interface
 */
export interface CacheValidityResult {
  isValid: boolean;
  expiresAt?: Date;
  cachedAt?: Date;
  remainingTtl?: number; // minutes
}

/**
 * Cache statistics interface
 */
export interface CacheStats {
  templates: {
    count: number;
    validCount: number;
    expiredCount: number;
  };
  categories: {
    count: number;
    validCount: number;
    expiredCount: number;
  };
  items: {
    count: number;
    validCount: number;
    expiredCount: number;
  };
  rooms: {
    count: number;
    validCount: number;
    expiredCount: number;
  };
}

/**
 * Startup sync status interface
 */
export interface StartupSyncStatus {
  isComplete: boolean;
  inProgress: boolean;
  templatesSync: boolean;
  categoriesSync: boolean;
  itemsSync: boolean;
  roomsSync: boolean;
  errors: string[];
}

/**
 * Default cache configuration
 */
const DEFAULT_CACHE_CONFIG: CacheConfig = {
  templates: {
    ttlHours: 24, // Templates change infrequently
    autoRefresh: true,
  },
  categories: {
    ttlHours: 24, // Categories change infrequently
    autoRefresh: true,
  },
  items: {
    ttlHours: 24, // Items change infrequently
    autoRefresh: true,
  },
  rooms: {
    ttlHours: 6, // Rooms might be added during reviews
    autoRefresh: true,
  },
  reviews: {
    ttlHours: 1, // Reviews change frequently
    autoRefresh: true,
  },
};

class CacheService {
  private config: CacheConfig = DEFAULT_CACHE_CONFIG;
  private isOnline: boolean = true;
  private startupSyncStatus: StartupSyncStatus = {
    isComplete: false,
    inProgress: false,
    templatesSync: false,
    categoriesSync: false,
    itemsSync: false,
    roomsSync: false,
    errors: [],
  };
  private refreshPromises: Map<string, Promise<any>> = new Map();
  private pendingDefaultRoomsRefresh: Promise<void> | null = null;
  private defaultRoomsListener: (() => void) | undefined;
  private static readonly TEMPLATES_METADATA_KEY = 'templates';
  private pendingPropertyRefreshes: Map<number, Promise<void>> = new Map();
  private propertyRoomsListeners: Map<number, () => void> = new Map();

  constructor() {
    this.initializeNetworkListener();
  }

  /**
   * Initialize network state listener
   */
  private initializeNetworkListener(): void {
    NetInfo.addEventListener((state: NetInfoState) => {
      const wasOffline = !this.isOnline;
      this.isOnline = state.isConnected ?? false;

      // If we just came back online, trigger sync
      if (wasOffline && this.isOnline) {
        this.syncAllCaches().catch(error => {
        });
      }
    });
  }

  /**
   * Configure cache settings
   */
  configure(config: Partial<CacheConfig>): void {
    this.config = {
      ...this.config,
      ...config,
      templates: { ...this.config.templates, ...config.templates },
      categories: { ...this.config.categories, ...config.categories },
      items: { ...this.config.items, ...config.items },
      rooms: { ...this.config.rooms, ...config.rooms },
      reviews: { ...this.config.reviews, ...config.reviews },
    };
  }

  /**
   * Initialize cache service and perform startup sync
   */
  async initialize(): Promise<void> {
    try {

      // Initialize the offline database
      await offlineDatabase.initialize();

      // Clear any expired cache entries
      await this.clearExpiredCaches();

      // Perform initial sync if online
      if (this.isOnline) {
        await this.performStartupSync();
      }

    } catch (error) {
      throw new Error(`Failed to initialize cache service: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Perform startup sync of all caches
   */
  private async performStartupSync(): Promise<void> {
    if (this.startupSyncStatus.inProgress) {
      return;
    }

    try {
      this.startupSyncStatus = {
        isComplete: false,
        inProgress: true,
        templatesSync: false,
        categoriesSync: false,
        itemsSync: false,
        roomsSync: false,
        errors: [],
      };

      // Sync templates first (they're needed for other data)
      await this.syncTemplatesIfNeeded();
      this.startupSyncStatus.templatesSync = true;

      // Ensure default rooms are cached (immediately if online, queued if offline)
      try {
        await this.ensureDefaultRoomsCached();
      } catch (error) {
        this.startupSyncStatus.errors.push(`Default rooms sync failed: ${error instanceof Error ? error.message : 'Unknown'}`);
      }

      // Sync categories and items in parallel
      const [categoriesResult, itemsResult] = await Promise.allSettled([
        this.syncCategoriesIfNeeded(),
        this.syncItemsIfNeeded(),
      ]);

      // Track sync results
      if (categoriesResult.status === 'fulfilled') {
        this.startupSyncStatus.categoriesSync = true;
      } else {
        this.startupSyncStatus.errors.push(`Categories sync failed: ${categoriesResult.reason}`);
      }

      if (itemsResult.status === 'fulfilled') {
        this.startupSyncStatus.itemsSync = true;
      } else {
        this.startupSyncStatus.errors.push(`Items sync failed: ${itemsResult.reason}`);
      }

      this.startupSyncStatus.isComplete = true;
      this.startupSyncStatus.inProgress = false;

      const syncedCount = [
        this.startupSyncStatus.templatesSync,
        this.startupSyncStatus.categoriesSync,
        this.startupSyncStatus.itemsSync,
        this.startupSyncStatus.roomsSync,
      ].filter(Boolean).length;

    } catch (error) {
      this.startupSyncStatus.inProgress = false;
      this.startupSyncStatus.errors.push(`Startup sync error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  /**
   * Get startup sync status
   */
  getStartupSyncStatus(): StartupSyncStatus {
    return { ...this.startupSyncStatus };
  }

  // ====================
  // TEMPLATES CACHE
  // ====================

  /**
   * Get cached templates or fetch from API if expired/missing
   */
  async getTemplates(forceRefresh: boolean = false): Promise<ReviewTemplate[]> {
    try {
      // Ensure database is initialized
      if (offlineDatabase && !offlineDatabase.isInitialized) {
          // console.error('[CacheService] Initializing offline database before getting templates');
        await offlineDatabase.initialize();
      }

      // Also ensure cache service itself is initialized
      if (!this.config) {
          // console.error('[CacheService] Cache service not configured, initializing...');
        await this.initialize();
      }

      if (!forceRefresh) {
        // Try to get from cache first
        const cachedTemplates = await offlineDatabase.getCachedTemplates();
        if (cachedTemplates.length > 0) {
            // console.error('[CacheService] Retrieved templates from cache:', cachedTemplates.length);
          return cachedTemplates;
        }
          // console.error('[CacheService] No templates found in cache');
      }

      // If not in cache or force refresh, fetch from API
      if (!this.isOnline) {
          // console.error('[CacheService] Offline and no cached templates available');
        // Try harder to get cached templates even if expired
        try {
          const expiredTemplates = await this.getExpiredTemplates();
          if (expiredTemplates.length > 0) {
              // console.error('[CacheService] Returning expired templates as last resort:', expiredTemplates.length);
            return expiredTemplates;
          }
        } catch (e) {
            // console.error('[CacheService] Could not get expired templates:', e);
        }
        return [];
      }

      // Fetch directly from API to avoid circular dependency
      return await this.refreshTemplates();
    } catch (error) {
        // console.error('[CacheService] Error getting templates:', error);
      // Try to get from cache as fallback
      try {
        const cachedTemplates = await offlineDatabase.getCachedTemplates();
        if (cachedTemplates.length > 0) {
            // console.error('[CacheService] Returning cached templates as fallback');
          return cachedTemplates;
        }
      } catch (cacheError) {
          // console.error('[CacheService] Cache fallback also failed:', cacheError);
      }
      return [];
    }
  }

  /**
   * Get templates even if expired (for last resort offline mode)
   */
  private async getExpiredTemplates(): Promise<ReviewTemplate[]> {
    try {
      // Query templates without expiry check
      const result = await offlineDatabase.executeSql(
        'SELECT * FROM templates_cache ORDER BY id',
        []
      );

      const templates: ReviewTemplate[] = [];
      for (let i = 0; i < result.rows.length; i++) {
        const row = result.rows.item(i);
        const template = JSON.parse(row.data);
        templates.push(template);
      }

        // console.error('[CacheService] Found expired templates:', templates.length);
      return templates;
    } catch (error) {
        // console.error('[CacheService] Error getting expired templates:', error);
      return [];
    }
  }

  /**
   * Refresh templates from API and update cache
   */
  async refreshTemplates(): Promise<ReviewTemplate[]> {
    const cacheKey = 'templates';

    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromises.has(cacheKey)) {
      return this.refreshPromises.get(cacheKey);
    }

    const refreshPromise = this.doRefreshTemplates();
    this.refreshPromises.set(cacheKey, refreshPromise);

    try {
      const result = await refreshPromise;
      return result;
    } finally {
      this.refreshPromises.delete(cacheKey);
    }
  }

  private async doRefreshTemplates(): Promise<ReviewTemplate[]> {
    if (!this.isOnline) {
      throw new Error('Cannot refresh templates while offline');
    }

    try {
      // Call API directly to avoid circular dependency
      const { api } = require('./api') as typeof import('./api');
      let templates = await api.get<ReviewTemplate[]>(FULL_REVIEW_TEMPLATES_PATH);

      // Validate the response before caching
      if (!templates || !Array.isArray(templates) || templates.length === 0) {
        // Return existing cache instead of empty array
        const existingTemplates = await offlineDatabase.getCachedTemplates();
        return existingTemplates;
      }

      // Cache the explicitly requested full templates
      await offlineDatabase.cacheTemplates(templates, this.config.templates.ttlHours);
      await this.updateTemplatesCacheMetadata();
      return templates;
    } catch (error) {
      // On API error, return existing cache instead of failing
      try {
        const existingTemplates = await offlineDatabase.getCachedTemplates();
        return existingTemplates;
      } catch (cacheError) {
        return [];
      }
    }
  }

  /**
   * Sync templates if needed (cache is expired or missing)
   */
  private async syncTemplatesIfNeeded(): Promise<void> {
    const validity = await this.checkTemplatesCacheValidity();
    if (!validity.isValid) {
      await this.refreshTemplates();
    }
  }

  /**
   * Check if templates cache is valid
   */
  async checkTemplatesCacheValidity(): Promise<CacheValidityResult> {
    try {
      const cachedTemplates = await offlineDatabase.getCachedTemplates();
      if (cachedTemplates.length === 0) {
        return { isValid: false };
      }

      const metadata = await offlineDatabase.getCacheMetadata?.(CacheService.TEMPLATES_METADATA_KEY);
      if (!metadata) {
        return { isValid: false };
      }

      const maxAgeMs = this.config.templates.ttlHours * 60 * 60 * 1000;
      const cacheAgeMs = Date.now() - metadata.lastUpdated.getTime();
      const isValid = cacheAgeMs < maxAgeMs;

      return {
        isValid,
        cachedAt: metadata.lastUpdated,
        expiresAt: metadata.expiresAt,
        remainingTtl: Math.max(0, Math.round((maxAgeMs - cacheAgeMs) / (60 * 1000))),
      };
    } catch (error) {
      return { isValid: false };
    }
  }

  /**
   * Check if templates cache is older than max age
   */
  async isTemplatesStale(maxAgeMinutes: number): Promise<boolean> {
    try {
      const metadata = await offlineDatabase.getCacheMetadata?.(CacheService.TEMPLATES_METADATA_KEY);
      if (!metadata) {
        return true;
      }

      const maxAgeMs = maxAgeMinutes * 60 * 1000;
      const cacheAgeMs = Date.now() - metadata.lastUpdated.getTime();
      return cacheAgeMs > maxAgeMs;
    } catch (error) {
      return true;
    }
  }

  private async updateTemplatesCacheMetadata(): Promise<void> {
    try {
      await offlineDatabase.updateCacheMetadata?.(CacheService.TEMPLATES_METADATA_KEY, new Date());
    } catch (error) {
      // Non-fatal: metadata write failure should not block template refresh
    }
  }

  // ====================
  // CATEGORIES CACHE
  // ====================

  /**
   * Get cached categories or fetch from API if expired/missing
   */
  async getCategories(templateId?: number, forceRefresh: boolean = false): Promise<ReviewCategory[]> {
    try {
      if (!forceRefresh) {
        const cachedCategories = await offlineDatabase.getCachedCategories(templateId);
        if (cachedCategories.length > 0) {
          return cachedCategories;
        }
      }

      if (!this.isOnline) {
        return [];
      }

      return await this.refreshCategories(templateId);
    } catch (error) {
      try {
        const cachedCategories = await offlineDatabase.getCachedCategories(templateId);
        return cachedCategories;
      } catch (cacheError) {
        return [];
      }
    }
  }

  /**
   * Refresh categories from API and update cache
   */
  async refreshCategories(templateId?: number): Promise<ReviewCategory[]> {
    const cacheKey = `categories_${templateId || 'all'}`;

    if (this.refreshPromises.has(cacheKey)) {
      return this.refreshPromises.get(cacheKey);
    }

    const refreshPromise = this.doRefreshCategories(templateId);
    this.refreshPromises.set(cacheKey, refreshPromise);

    try {
      return await refreshPromise;
    } finally {
      this.refreshPromises.delete(cacheKey);
    }
  }

  private async doRefreshCategories(templateId?: number): Promise<ReviewCategory[]> {
    if (!this.isOnline) {
      throw new Error('Cannot refresh categories while offline');
    }

    // Get templates first to extract categories
    const templates = await this.getTemplates();
    let allCategories: ReviewCategory[] = [];

    for (const template of templates) {
      if (template.categories) {
        allCategories.push(...template.categories);
      }
    }

    // Filter by templateId if specified
    const categories = templateId
      ? allCategories.filter(cat => cat.templateId === templateId)
      : allCategories;

    // Cache the categories
    await offlineDatabase.cacheCategories(categories, this.config.categories.ttlHours);

    return categories;
  }

  /**
   * Sync categories if needed
   */
  private async syncCategoriesIfNeeded(): Promise<void> {
    const validity = await this.checkCategoriesCacheValidity();
    if (!validity.isValid) {
      await this.refreshCategories();
    }
  }

  /**
   * Check if categories cache is valid
   */
  async checkCategoriesCacheValidity(templateId?: number): Promise<CacheValidityResult> {
    try {
      const cachedCategories = await offlineDatabase.getCachedCategories(templateId);
      if (cachedCategories.length === 0) {
        return { isValid: false };
      }

      return {
        isValid: true,
        cachedAt: new Date(),
        expiresAt: new Date(Date.now() + this.config.categories.ttlHours * 60 * 60 * 1000),
        remainingTtl: this.config.categories.ttlHours * 60,
      };
    } catch (error) {
      return { isValid: false };
    }
  }

  // ====================
  // ITEMS CACHE
  // ====================

  /**
   * Get cached items or fetch from API if expired/missing
   */
  async getItems(categoryId?: number, forceRefresh: boolean = false): Promise<ReviewItem[]> {
    try {
      if (!forceRefresh) {
        const cachedItems = await offlineDatabase.getCachedItems(categoryId);
        if (cachedItems.length > 0) {
          return cachedItems;
        }
      }

      if (!this.isOnline) {
        return [];
      }

      return await this.refreshItems(categoryId);
    } catch (error) {
      try {
        const cachedItems = await offlineDatabase.getCachedItems(categoryId);
        return cachedItems;
      } catch (cacheError) {
        return [];
      }
    }
  }

  /**
   * Refresh items from API and update cache
   */
  async refreshItems(categoryId?: number): Promise<ReviewItem[]> {
    const cacheKey = `items_${categoryId || 'all'}`;

    if (this.refreshPromises.has(cacheKey)) {
      return this.refreshPromises.get(cacheKey);
    }

    const refreshPromise = this.doRefreshItems(categoryId);
    this.refreshPromises.set(cacheKey, refreshPromise);

    try {
      return await refreshPromise;
    } finally {
      this.refreshPromises.delete(cacheKey);
    }
  }

  private async doRefreshItems(categoryId?: number): Promise<ReviewItem[]> {
    if (!this.isOnline) {
      throw new Error('Cannot refresh items while offline');
    }


    // Get categories first to extract items
    const categories = await this.getCategories();
    let allItems: ReviewItem[] = [];

    for (const category of categories) {
      if (category.items) {
        allItems.push(...category.items);
      }
    }

    // Filter by categoryId if specified
    const items = categoryId
      ? allItems.filter(item => item.categoryId === categoryId)
      : allItems;

    // Cache the items
    await offlineDatabase.cacheItems(items, this.config.items.ttlHours);

    return items;
  }

  /**
   * Sync items if needed
   */
  private async syncItemsIfNeeded(): Promise<void> {
    const validity = await this.checkItemsCacheValidity();
    if (!validity.isValid) {
      await this.refreshItems();
    }
  }

  /**
   * Check if items cache is valid
   */
  async checkItemsCacheValidity(categoryId?: number): Promise<CacheValidityResult> {
    try {
      const cachedItems = await offlineDatabase.getCachedItems(categoryId);
      if (cachedItems.length === 0) {
        return { isValid: false };
      }

      return {
        isValid: true,
        cachedAt: new Date(),
        expiresAt: new Date(Date.now() + this.config.items.ttlHours * 60 * 60 * 1000),
        remainingTtl: this.config.items.ttlHours * 60,
      };
    } catch (error) {
      return { isValid: false };
    }
  }

  // ====================
  // ROOMS CACHE
  // ====================

  /**
   * Ensure default rooms are cached (queue if offline)
   */
  private async ensureDefaultRoomsCached(): Promise<void> {
    if (this.isOnline) {
      // Online: fetch immediately
      try {
        await this.refreshRooms();
        this.startupSyncStatus.roomsSync = true;
      } catch (error) {
        console.error('[CacheService] ❌ Failed to fetch default rooms:', error);
        throw error;
      } finally {
        // Clear listener reference and pending promise (avoid dangling refs)
        this.defaultRoomsListener = undefined;
        this.pendingDefaultRoomsRefresh = null;
      }
      return;
    }

    // Offline: queue for next online event (only create listener once)
    if (!this.pendingDefaultRoomsRefresh) {
      this.pendingDefaultRoomsRefresh = new Promise<void>((resolve) => {
        this.defaultRoomsListener = NetInfo.addEventListener(state => {
          if (state.isConnected) {
            // Update isOnline flag BEFORE attempting refresh
            this.isOnline = state.isConnected ?? false;

            // Cleanup listener
            this.defaultRoomsListener?.();
            this.defaultRoomsListener = undefined;

            // Fetch rooms (will now succeed because isOnline is true)
            this.refreshRooms()
              .then(() => {
                this.startupSyncStatus.roomsSync = true;
              })
              .catch(err => {
                console.error('[CacheService] ❌ Failed to fetch queued default rooms:', err);
              })
              .finally(() => {
                this.pendingDefaultRoomsRefresh = null;
                resolve();
              });
          }
        });
      });
    }

    return this.pendingDefaultRoomsRefresh;
  }

  /**
   * Queue property-specific rooms refresh for next online event
   */
  async queuePropertyRoomsRefresh(propertyId: number): Promise<void> {
    // If already queued, return existing promise
    if (this.pendingPropertyRefreshes.has(propertyId)) {
      return this.pendingPropertyRefreshes.get(propertyId)!;
    }

    const refreshPromise = new Promise<void>((resolve) => {
      const listener = NetInfo.addEventListener(state => {
        if (state.isConnected) {
          // Update isOnline flag BEFORE attempting refresh
          this.isOnline = state.isConnected ?? false;

          // Cleanup listener
          listener();
          this.propertyRoomsListeners.delete(propertyId);

          // Fetch rooms (will now succeed because isOnline is true)
          this.refreshRooms(propertyId)
            .then(() => {
              // Success - rooms cached
            })
            .catch(err => {
              const is404 = err.message?.includes('Resource not found');
              if (!is404) {
                // Property just created, not on server yet - will retry on next online event
                console.error('[CacheService] ❌ Failed to fetch queued property rooms:', propertyId, err);
              }
            })
            .finally(() => {
              // Remove from pending in all exit paths (success or failure)
              this.pendingPropertyRefreshes.delete(propertyId);
              resolve();
            });
        }
      });

      this.propertyRoomsListeners.set(propertyId, listener);
    });

    this.pendingPropertyRefreshes.set(propertyId, refreshPromise);
    return refreshPromise;
  }

  /**
   * Get cached rooms or fetch from API if expired/missing
   */
  async getRooms(propertyId?: number, forceRefresh: boolean = false): Promise<Room[]> {
    try {
      if (!forceRefresh) {
        // Always merge: defaults + property-specific + pending offline rooms
        const defaultRooms = await offlineDatabase.getCachedRooms(undefined).catch(() => []); // Global defaults
        const propertyRooms = propertyId ? await offlineDatabase.getCachedRooms(propertyId).catch(() => []) : [];

        // Get pending offline rooms for this property
        let offlineRooms: Room[] = [];
        if (offlineDatabase.getPendingOfflineRooms) {
          try {
            const pendingRooms = await offlineDatabase.getPendingOfflineRooms();
            offlineRooms = pendingRooms
              .filter(r => r.property_id === propertyId)
              .map(r => ({
                id: safeNegativeId(r.temp_id), // Safe negative ID within MAX_SAFE_INTEGER
                propertyId: r.property_id,
                name: r.name,
                displayOrder: r.display_order || 0,
                isCustom: true,
                tempId: r.temp_id,
              } as Room));
          } catch (error) {
            console.error('[CacheService] Error getting pending offline rooms:', error);
          }
        }

        // Merge all three sources with deduplication by tempId or id
        const roomsMap = new Map<string, Room>();

        // Add defaults first (lowest priority)
        defaultRooms.forEach(room => {
          const key = room.tempId || `id_${room.id}`;
          roomsMap.set(key, room);
        });

        // Add property-specific rooms (override defaults if same ID)
        propertyRooms.forEach(room => {
          const key = room.tempId || `id_${room.id}`;
          roomsMap.set(key, room);
        });

        // Add pending offline rooms only if not already in cache (highest priority)
        offlineRooms.forEach(room => {
          const key = room.tempId || `id_${room.id}`;
          // Only add if not already cached (prevents duplicates after partial sync)
          if (!roomsMap.has(key)) {
            roomsMap.set(key, room);
          }
        });

        const mergedRooms = Array.from(roomsMap.values());

        // If property was requested but not found in cache, fetch it
        if (propertyId && propertyRooms.length === 0) {
          if (this.isOnline) {
            try {
              await this.refreshRooms(propertyId);

              // Re-read from SQLite and re-merge (single point of dedupe)
              const refreshedPropertyRooms = await offlineDatabase.getCachedRooms(propertyId).catch(() => []);

              // Re-run the merge with fresh data
              const roomsMap = new Map<string, Room>();

              defaultRooms.forEach(room => {
                const key = room.tempId || `id_${room.id}`;
                roomsMap.set(key, room);
              });

              refreshedPropertyRooms.forEach(room => {
                const key = room.tempId || `id_${room.id}`;
                roomsMap.set(key, room);
              });

              offlineRooms.forEach(room => {
                const key = room.tempId || `id_${room.id}`;
                if (!roomsMap.has(key)) {
                  roomsMap.set(key, room);
                }
              });

              return Array.from(roomsMap.values());
            } catch (error) {
              console.error('[CacheService] Failed to fetch property rooms:', error);
              // Fall through to return defaults
            }
          } else {
            // Queue for next online event, but return defaults for now
            this.queuePropertyRoomsRefresh(propertyId).catch(() => {});
          }
        }

        if (mergedRooms.length > 0) {
          return mergedRooms;
        }
      }

      if (!this.isOnline) {
        return [];
      }

      return await this.refreshRooms(propertyId);
    } catch (error) {
      console.error('[CacheService] ❌ Error getting rooms:', error);
      // Try defaults as last resort
      try {
        return await offlineDatabase.getCachedRooms(undefined).catch(() => []);
      } catch {
        return [];
      }
    }
  }

  /**
   * Refresh rooms from API and update cache
   */
  async refreshRooms(propertyId?: number): Promise<Room[]> {
    const cacheKey = `rooms_${propertyId || 'default'}`;

    if (this.refreshPromises.has(cacheKey)) {
      return this.refreshPromises.get(cacheKey);
    }

    const refreshPromise = this.doRefreshRooms(propertyId);
    this.refreshPromises.set(cacheKey, refreshPromise);

    try {
      return await refreshPromise;
    } finally {
      this.refreshPromises.delete(cacheKey);
    }
  }

  private async doRefreshRooms(propertyId?: number): Promise<Room[]> {
    if (!this.isOnline) {
      throw new Error('Cannot refresh rooms while offline');
    }

    // Call API directly to avoid circular dependency
    const { api } = require('./api') as typeof import('./api');
    const url = propertyId
      ? `/rooms/property/${propertyId}`
      : '/rooms/defaults';

    let rooms = await api.get<Room[]>(url);

    // If fetching property rooms, filter out defaults (they're already cached separately)
    // This avoids UNIQUE constraint violation on room.id
    if (propertyId) {
      rooms = rooms.filter(room => room.propertyId === propertyId);
    }

    // Cache the rooms
    await offlineDatabase.cacheRooms(rooms, propertyId, this.config.rooms.ttlHours);

    return rooms;
  }

  /**
   * Sync rooms if needed
   */
  private async syncRoomsIfNeeded(): Promise<void> {
    const validity = await this.checkRoomsCacheValidity();
    if (!validity.isValid) {
      await this.refreshRooms(); // Get default rooms
    }
  }

  /**
   * Check if rooms cache is valid
   */
  async checkRoomsCacheValidity(propertyId?: number): Promise<CacheValidityResult> {
    try {
      const cachedRooms = await offlineDatabase.getCachedRooms(propertyId);
      if (cachedRooms.length === 0) {
        return { isValid: false };
      }

      return {
        isValid: true,
        cachedAt: new Date(),
        expiresAt: new Date(Date.now() + this.config.rooms.ttlHours * 60 * 60 * 1000),
        remainingTtl: this.config.rooms.ttlHours * 60,
      };
    } catch (error) {
      return { isValid: false };
    }
  }

  // ====================
  // CACHE MANAGEMENT
  // ====================

  /**
   * Invalidate specific cache type
   */
  async invalidateCache(cacheType: 'templates' | 'categories' | 'items' | 'rooms' | 'all'): Promise<void> {
    try {
      switch (cacheType) {
        case 'templates':
          await offlineDatabase.cacheTemplates([], 0); // Empty cache with 0 TTL
          break;
        case 'categories':
          await offlineDatabase.cacheCategories([], 0);
          break;
        case 'items':
          await offlineDatabase.cacheItems([], 0);
          break;
        case 'rooms':
          await offlineDatabase.cacheRooms([], undefined, 0);
          break;
        case 'all':
          await Promise.all([
            offlineDatabase.cacheTemplates([], 0),
            offlineDatabase.cacheCategories([], 0),
            offlineDatabase.cacheItems([], 0),
            offlineDatabase.cacheRooms([], undefined, 0),
          ]);
          break;
      }

    } catch (error) {
      throw error;
    }
  }

  /**
   * Clear all expired cache entries
   */
  async clearExpiredCaches(): Promise<void> {
    try {
      await offlineDatabase.clearExpiredCache();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Force refresh all caches from API
   */
  async syncAllCaches(): Promise<void> {
    if (!this.isOnline) {
      return;
    }

    try {
      // Sync in order: templates first, then others in parallel
      await this.refreshTemplates();

      await Promise.all([
        this.refreshCategories(),
        this.refreshItems(),
        this.refreshRooms(),
      ]);

    } catch (error) {
      throw error;
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<CacheStats> {
    try {
      const dbStats = await offlineDatabase.getDatabaseStats();

      return {
        templates: {
          count: dbStats.cachedTemplates,
          validCount: dbStats.cachedTemplates, // Database already filters expired
          expiredCount: 0,
        },
        categories: {
          count: dbStats.cachedCategories,
          validCount: dbStats.cachedCategories,
          expiredCount: 0,
        },
        items: {
          count: dbStats.cachedItems,
          validCount: dbStats.cachedItems,
          expiredCount: 0,
        },
        rooms: {
          count: dbStats.cachedRooms,
          validCount: dbStats.cachedRooms,
          expiredCount: 0,
        },
      };
    } catch (error) {
      return {
        templates: { count: 0, validCount: 0, expiredCount: 0 },
        categories: { count: 0, validCount: 0, expiredCount: 0 },
        items: { count: 0, validCount: 0, expiredCount: 0 },
        rooms: { count: 0, validCount: 0, expiredCount: 0 },
      };
    }
  }

  /**
   * Check if the cache service is online
   */
  isServiceOnline(): boolean {
    return this.isOnline;
  }

  /**
   * Get current cache configuration
   */
  getConfiguration(): CacheConfig {
    return { ...this.config };
  }

  /**
   * Cleanup listeners and pending refreshes
   */
  cleanup(): void {
    // Cleanup default rooms listener
    this.defaultRoomsListener?.();
    this.defaultRoomsListener = undefined;
    this.pendingDefaultRoomsRefresh = null;

    // Cleanup property rooms listeners
    this.propertyRoomsListeners.forEach(listener => listener());
    this.propertyRoomsListeners.clear();
    this.pendingPropertyRefreshes.clear();
  }
}

// Export singleton instance
export const cacheService = new CacheService();
